import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CareMapDrawerPage } from '../../components/CareMapDrawerPage';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { CareMapDrawerState } from '../../caremap/CareMapDrawerState';
import { CareMapState } from '../../caremap/CareMapState';
import { CareMapDrawerTrackingHelper } from '../../caremap/CareMapDrawerTrackingHelper';
import { IonButton } from '@ionic/react';
import { ActiveCaremapNoteEditor } from './ActiveCaremapNoteEditor';
import { useNoteEditor } from './useNoteEditor';

export const EditNoteUsingCareMapDrawer: React.FC<{noteId: string; }> = ({ noteId }) => {
    const history = useInstance(HistoryViewModel);
    const drawerState = useInstance(CareMapDrawerState);
    const careMapState = useInstance(CareMapState);
    const careMapDrawerTrackingHelp = useInstance(CareMapDrawerTrackingHelper);

    useSubscription(() => drawerState);
    useSubscription(() => careMapDrawerTrackingHelp);

    const { noteVM, saveAndClose } = useNoteEditor({
        noteId,
        trackingFrom: careMapDrawerTrackingHelp.state.trackingFrom,
        onSaved: () => history.back(),
        navigateToCommunity: () => {
            // close the drawer and make sure focused caremap is closed, then go to community
            history.history.push('/caremaps');
            setTimeout(() => history.push('/community-redirect'), 350);
        }
    });

    if (!noteVM) {
        return null;
    }

    const onDidEnter = () => {
        drawerState.setIsMaskingFocusedCareMap(true);
    };

    const onWillLeave = () => {
        careMapDrawerTrackingHelp.clearTrackingFrom();
        drawerState.setIsMaskingFocusedCareMap(false);
    };

    const canEdit = careMapState.hasPermissionTo('put-note');

    return (
        <CareMapDrawerPage
            stretchHeight
            title={!canEdit ? 'View Note' : 'Edit Note'}
            hasBack={!canEdit}
            hasClose={false}
            enteredHandler={onDidEnter}
            leavingHandler={onWillLeave}
            data-type="note-editor"
            buttons={canEdit && (
                <>
                    <IonButton
                        data-type="save-button"
                        disabled={!noteVM.canSave()}
                        onClick={saveAndClose}>Save</IonButton>
                    <IonButton data-type="cancel-button" fill="outline" onClick={() => {
                        history.back();
                    }}>Cancel</IonButton>
                </>
            )}
        >
            <ActiveCaremapNoteEditor
                noteVM={noteVM}
                onDeleted={() => history.back()}
                trackingFrom={careMapDrawerTrackingHelp.state.trackingFrom}
                excludeMenu={canEdit}
            />
        </CareMapDrawerPage>
    );
};
